import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { forwardRef } from "react";
import { tv } from "tailwind-variants";

const tableVariants = tv({
  slots: {
    root: "relative hidden w-full overflow-auto rounded-xl border border-secondary-100 transition-colors md:flex",
    table: "w-full text-sm",
    caption: "pb-4 text-left text-lg font-semibold",
    header: "",
    head: "h-14 bg-secondary-50 px-3 py-4 text-left align-middle font-medium text-neutral-950 first:pl-5 last:pr-5 [&:has([role=checkbox])]:pr-0",
    body: "",
    row: "odd:bg-white even:bg-neutral-50 hover:bg-neutral-100 data-[state=selected]:bg-neutral-300",
    cell: "h-14 px-3 py-4 align-middle text-neutral-950 first:pl-5 last:pr-5 [&:has([role=checkbox])]:pr-0",
    footer: "font-medium",
  },
});

const { root, table, caption, header, head, body, row, cell, footer } =
  tableVariants();

const Root = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className={root()}>
      <table ref={ref} className={table({ className })} {...props} />
    </div>
  ),
);
Root.displayName = "Table.Root";

const Caption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={caption({ className })} {...props} />
));
Caption.displayName = "Table.Caption";

const Header = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={header({ className })} {...props} />
));
Header.displayName = "Table.Header";

const Head = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th ref={ref} className={head({ className })} {...props} />
));
Head.displayName = "Table.Head";

const Body = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={body({ className })} {...props} />
));
Body.displayName = "Table.Body";

const Row = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr ref={ref} className={row({ className })} {...props} />
));
Row.displayName = "Table.Row";

const Cell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={cell({ className })} {...props} />
));
Cell.displayName = "Table.Cell";

const Footer = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot ref={ref} className={footer({ className })} {...props} />
));
Footer.displayName = "Table.Footer";

export const Table = {
  Root,
  Header,
  Body,
  Footer,
  Row,
  Head,
  Cell,
  Caption,
};
